import React from 'react';
import { ExamInfoTabProps } from '../ExamInfoTab';
import { Button } from 'react-bootstrap';
import { SmsSignModal, ExamStatus } from 'components';
import { IApiError } from '_helpers/ApiConnector/types';
import styles from './SendProtocolModal.module.scss';
import moment from 'moment';

const SendProtocolModal = ({
    examDetails,
    authorizedExpert,
    smsCodeStatus,
    onSendNokPasswordAgain,
    onSendAkPasswordAgain,
    onSendSovietPasswordAgain,
    onSubmitNokSignCode,
    onSubmitAkSignCode,
    onSubmitSovietSignCode,
    sendCodeProcessing,
    apiError,
    onSendCodeForSignCurrentSovietProtocols,
    onSubmitCurrentSovietProtocols,
    buttonText,
    modalTitle,
    modalBodyText,
}: IProps) => {
    const [showSignModal, setShowSignModal] = React.useState(false);

    const isSignedByCurrentExpertForCurrentStatus = (): boolean => {
        if (!authorizedExpert || !examDetails) return false;
        switch (examDetails.status) {
            case ExamStatus.FinalizedResults:
                return examDetails.experts.some(expert => expert.expertId === authorizedExpert.id && expert.signed);
            case ExamStatus.NokProtocolSigned:
                return examDetails.akExperts.some(expert => expert.expertId === authorizedExpert.id && expert.signed);
            case ExamStatus.AkProtocolSigned:
            case ExamStatus.SovietProtocolSignedByMembers:
                return examDetails.sovietExperts.some(expert => expert.expertId === authorizedExpert.id && expert.signed);
            default:
                return false;
        }
    };

    const isExamExpertForCurrentStatus = (): boolean => {
        if (!authorizedExpert || !examDetails) return false;
        switch (examDetails.status) {
            case ExamStatus.FinalizedResults:
                return examDetails.experts.some(expert => expert.expertId === authorizedExpert.id);
            case ExamStatus.NokProtocolSigned:
                return examDetails.akExperts.some(expert => expert.expertId === authorizedExpert.id);
            case ExamStatus.AkProtocolSigned:
                return examDetails.sovietExperts.some(expert => expert.expertId === authorizedExpert.id && !expert.chairman);
            case ExamStatus.SovietProtocolSignedByMembers:
                return examDetails.sovietExperts.some(expert => expert.expertId === authorizedExpert.id && expert.chairman);
            default:
                return false;
        }
    };

    const handleCloseSignProtocol = () => setShowSignModal(false);
    const isFinalized = () => examDetails && examDetails.status === ExamStatus.FinalizedResults;
    const isNokSigned = () => examDetails && examDetails.status === ExamStatus.NokProtocolSigned;
    const isAkOrSovietSigned = () => examDetails && (examDetails.status === ExamStatus.AkProtocolSigned || examDetails.status === ExamStatus.SovietProtocolSignedByMembers);

    const handleSendPasswordAgain = async (captcha?: string) => {
        let res: ISmsCodeStatus | null = null;

        if (!examDetails) {
            if (!onSendCodeForSignCurrentSovietProtocols) return null;

            res = await onSendCodeForSignCurrentSovietProtocols({ captcha });

            return res;
        }

        if (isFinalized() && onSendNokPasswordAgain) {
            res = await onSendNokPasswordAgain({ examId: examDetails.id, captcha });
        } else if (isNokSigned() && onSendAkPasswordAgain) {
            res = await onSendAkPasswordAgain({ examId: examDetails.id, captcha });
        } else if (isAkOrSovietSigned() && onSendSovietPasswordAgain) {
            res = await onSendSovietPasswordAgain({ examId: examDetails.id, captcha });
        }

        return res;
    };

    const handleOpenSignProtocol = () => {
        setShowSignModal(true);
        if (!smsCodeStatus) {
            handleSendPasswordAgain('');
        }
    };

    const handleSubmitCode = async (code: string, captcha?: string) => {
        let res: boolean | null = false;

        if (!examDetails) {
            if (onSubmitCurrentSovietProtocols) {
                res = await onSubmitCurrentSovietProtocols({ code, captcha });
            }
        } else if (isFinalized() && onSubmitNokSignCode) {
            res = await onSubmitNokSignCode({
                examId: examDetails.id,
                code,
                captcha,
            });
        } else if (isNokSigned() && onSubmitAkSignCode) {
            res = await onSubmitAkSignCode({
                examId: examDetails.id,
                code,
                captcha,
            });
        } else if (isAkOrSovietSigned() && onSubmitSovietSignCode) {
            res = await onSubmitSovietSignCode({
                examId: examDetails.id,
                code,
                captcha,
            });
        }

        if (res) {
            handleCloseSignProtocol();
        }
    };

    const checkShowButton = Boolean(!isSignedByCurrentExpertForCurrentStatus() && isExamExpertForCurrentStatus())
        || Boolean(!examDetails && onSendCodeForSignCurrentSovietProtocols);

    return (
        <div>
            {checkShowButton ? (
                <Button className={styles.button} onClick={handleOpenSignProtocol}>
                    {buttonText || 'Подписать протокол электронно'}
                </Button>
            ) : null}
            <SmsSignModal
              onHide={handleCloseSignProtocol}
              onSendPasswordAgain={handleSendPasswordAgain}
              phone={String(authorizedExpert && authorizedExpert.phone)}
              show={showSignModal}
              onSubmit={handleSubmitCode}
              apiError={apiError}
              processing={sendCodeProcessing}
              waitingSeconds={smsCodeStatus && smsCodeStatus.waitingSeconds}
              title={modalTitle}
              phoneText={modalBodyText}
            />
        </div>
    );
};

export default SendProtocolModal;

interface IProps extends ISendProtocolProps {
    examDetails?: ExamInfoTabProps['examDetails'];
    buttonText?: string;
    modalTitle?: string;
    modalBodyText?: string;
}

export interface ISendProtocolProps {
    sendCodeProcessing: boolean;
    apiError: IApiError | null | undefined;
    authorizedExpert?: IAuthorizedExpert | null;
    smsCodeStatus?: ISmsCodeStatus | null;
    onSendNokPasswordAgain?: (data: ISendPasswordAgain) => Promise<ISmsCodeStatus | null>;
    onSendAkPasswordAgain?: (data: ISendPasswordAgain) => Promise<ISmsCodeStatus | null>;
    onSendSovietPasswordAgain?: (data: ISendPasswordAgain) => Promise<ISmsCodeStatus | null>;
    onSendCodeForSignCurrentSovietProtocols?: ({ captcha }: { captcha?: string }) => Promise<ISmsCodeStatus | null>;
    onSubmitNokSignCode?: (data: ISendCode) => Promise<boolean>;
    onSubmitAkSignCode?: (data: ISendCode) => Promise<boolean>;
    onSubmitSovietSignCode?: (data: ISendCode) => Promise<boolean>;
    onSubmitCurrentSovietProtocols?: (data: Omit<ISendCode, 'examId'>) => Promise<boolean | null>;
}

interface IAuthorizedExpert {
    id: string;
    regNumber: string;
    firstName: string;
    lastName: string;
    middleName: string;
    birthDate: moment.Moment;
    phone: string;
    email: string;
    regionId: number;
    region: string;
    sovietMember: boolean;
    akMember: boolean;
    nokMember: boolean;
    chairman: boolean;
    employee?: boolean;
    emailConfirmed: boolean;
    registrationCompleted: boolean;
    position?: string;
    applicantId?: string;
}

interface ISmsCodeStatus {
    wasSent?: boolean;
    waitingSeconds?: number;
}

interface ISendPasswordAgain {
    examId: string;
    captcha?: string;
}

interface ISendCode extends ISendPasswordAgain {
    code: string;
}
